import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../../utils/constants';
import './GuestsAddScreen.css';

function GuestsAddScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [guestTypes, setGuestTypes] = useState([]);
  
  const [formData, setFormData] = useState({
    guestNumber: '',
    guestType: '',
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipcode: '',
    country: 'USA',
    phone1: '',
    phone2: '',
    password: '',
    confirmPassword: '',
    email: ''
  });

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchGuestTypes();
    generateGuestNumber();
  }, []);

  const fetchGuestTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/guest-types`);
      if (response.ok) {
        const data = await response.json();
        setGuestTypes(data);
        if (data.length > 0) {
          setFormData(prev => ({ ...prev, guestType: data[0].name }));
        }
      }
    } catch (error) {
      console.error('Error fetching guest types:', error);
    }
  };

  const generateGuestNumber = () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    setFormData(prev => ({ ...prev, guestNumber: `G${timestamp}${random}` }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.guestNumber.trim()) {
      errors.guestNumber = 'Guest number is required';
    }
    
    if (!formData.guestType) {
      errors.guestType = 'Guest type is required';
    }
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters long';
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const guestData = {
        guestnumber: formData.guestNumber,
        guesttype: formData.guestType,
        firstname: formData.firstName,
        lastname: formData.lastName,
        address1: formData.address1 || null,
        address2: formData.address2 || null,
        city: formData.city || null,
        state: formData.state || null,
        zipcode: formData.zipcode || null,
        country: formData.country || null,
        phone1: formData.phone1 || null,
        phone2: formData.phone2 || null,
        password: formData.password,
        email: formData.email
      };
      
      const response = await fetch(`${API_URL}/guests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(guestData),
      });
      
      if (response.ok) {
        const result = await response.json();
        setSuccess(`Guest ${result.guest.firstname} ${result.guest.lastname} created successfully!`);
        
        // Reset form after successful creation
        setTimeout(() => {
          navigate('/dashboard/guests-list');
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Error creating guest');
      }
    } catch (error) {
      console.error('Error creating guest:', error);
      setError('Error creating guest. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      guestNumber: '',
      guestType: '',
      firstName: '',
      lastName: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipcode: '',
      country: 'USA',
      phone1: '',
      phone2: '',
      password: '',
      confirmPassword: '',
      email: ''
    });
    setValidationErrors({});
    setError('');
    setSuccess('');
    generateGuestNumber();
  };

  const getFieldError = (fieldName) => {
    return validationErrors[fieldName] || '';
  };

  const isFieldInvalid = (fieldName) => {
    return !!validationErrors[fieldName];
  };

  return (
    <Container fluid className="guests-add-screen">
      <Row>
        <Col>
          <h1 className="mb-4">Add New Guest</h1>
          
          {/* Success Alert */}
          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}
          
          {/* Error Alert */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          
          <Form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <Card className="mb-4">
              <Card.Header>
                <h5>Basic Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Guest Number *</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="text"
                          name="guestNumber"
                          value={formData.guestNumber}
                          onChange={handleInputChange}
                          isInvalid={isFieldInvalid('guestNumber')}
                          placeholder="Guest number"
                        />
                        <Button 
                          type="button" 
                          variant="outline-secondary" 
                          onClick={generateGuestNumber}
                          title="Generate new guest number"
                        >
                          🔄
                        </Button>
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {getFieldError('guestNumber')}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Guest Type *</Form.Label>
                      <Form.Select
                        name="guestType"
                        value={formData.guestType}
                        onChange={handleInputChange}
                        isInvalid={isFieldInvalid('guestType')}
                      >
                        <option value="">Select guest type</option>
                        {guestTypes.map(type => (
                          <option key={type.id} value={type.name}>
                            {type.name}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {getFieldError('guestType')}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Country</Form.Label>
                      <Form.Select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                      >
                        <option value="USA">United States</option>
                        <option value="Canada">Canada</option>
                        <option value="Mexico">Mexico</option>
                        <option value="UK">United Kingdom</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>First Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        isInvalid={isFieldInvalid('firstName')}
                        placeholder="Enter first name"
                      />
                      <Form.Control.Feedback type="invalid">
                        {getFieldError('firstName')}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        isInvalid={isFieldInvalid('lastName')}
                        placeholder="Enter last name"
                      />
                      <Form.Control.Feedback type="invalid">
                        {getFieldError('lastName')}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Contact Information */}
            <Card className="mb-4">
              <Card.Header>
                <h5>Contact Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address *</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        isInvalid={isFieldInvalid('email')}
                        placeholder="Enter email address"
                      />
                      <Form.Control.Feedback type="invalid">
                        {getFieldError('email')}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Primary Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone1"
                        value={formData.phone1}
                        onChange={handleInputChange}
                        placeholder="Enter primary phone number"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Secondary Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone2"
                        value={formData.phone2}
                        onChange={handleInputChange}
                        placeholder="Enter secondary phone number"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Address Information */}
            <Card className="mb-4">
              <Card.Header>
                <h5>Address Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Address Line 1</Form.Label>
                      <Form.Control
                        type="text"
                        name="address1"
                        value={formData.address1}
                        onChange={handleInputChange}
                        placeholder="Enter street address"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Address Line 2</Form.Label>
                      <Form.Control
                        type="text"
                        name="address2"
                        value={formData.address2}
                        onChange={handleInputChange}
                        placeholder="Apartment, suite, etc. (optional)"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Enter city"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>State/Province</Form.Label>
                      <Form.Control
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="Enter state or province"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>ZIP/Postal Code</Form.Label>
                      <Form.Control
                        type="text"
                        name="zipcode"
                        value={formData.zipcode}
                        onChange={handleInputChange}
                        placeholder="Enter ZIP or postal code"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Account Security */}
            <Card className="mb-4">
              <Card.Header>
                <h5>Account Security</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Password *</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        isInvalid={isFieldInvalid('password')}
                        placeholder="Enter password (min 6 characters)"
                      />
                      <Form.Control.Feedback type="invalid">
                        {getFieldError('password')}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Confirm Password *</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        isInvalid={isFieldInvalid('confirmPassword')}
                        placeholder="Confirm password"
                      />
                      <Form.Control.Feedback type="invalid">
                        {getFieldError('confirmPassword')}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Form Actions */}
            <Card className="mb-4">
              <Card.Body>
                <div className="d-flex gap-3 justify-content-end">
                  <Button 
                    type="button" 
                    variant="secondary" 
                    onClick={handleReset}
                    disabled={loading}
                  >
                    Reset Form
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline-secondary" 
                    onClick={() => navigate('/dashboard/guests-list')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    variant="primary" 
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Creating Guest...
                      </>
                    ) : (
                      'Create Guest'
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Form>
        </Col>
      </Row>
    </Container>
  );
}

export default GuestsAddScreen;
